import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Boxes,
  AlertTriangle,
  CalendarDays,
  ShieldCheck,
  MapPin,
  Settings,
  LogOut,
  Truck,
  HandCoins,
  BookOpen,
  Package,
  ListChecks,
  FileText,
  Clock,
  Receipt,
} from "@/components/icons";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/Badges";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { PuntNavItem } from "@/components/PuntCapture";
import { PuntQuickDialog } from "@/components/PuntQuickDialog";
import { ShellContext } from "@/components/ShellContext";
import { BalkProvider } from "@/lib/balkState";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/assets", label: "Assets", icon: Boxes, testid: "nav-assets" },
  { to: "/storingen", label: "Storingen", icon: AlertTriangle, testid: "nav-storingen", module: "storingen" },
  { to: "/werkbonnen", label: "Werkbonnen", icon: FileText, testid: "nav-werkbonnen", module: "werkbonnen" },
  { to: "/uren", label: "Uren", icon: Clock, testid: "nav-uren", module: "uren" },
  { to: "/facturen", label: "Facturen", icon: Receipt, testid: "nav-facturen", roles: ["owner", "td"] },
  { to: "/kennisbank", label: "Kennisbank", icon: BookOpen, testid: "nav-kennisbank", module: "kennisbank" },
  { to: "/onderdelen", label: "Onderdelen", icon: Package, testid: "nav-onderdelen", module: "onderdelen" },
  { to: "/verhaalbaar", label: "Verhaalbaar", icon: HandCoins, testid: "nav-verhaalbaar", module: "verhaalbaar" },
  { to: "/mijn-werk", label: "Mijn werk", icon: ListChecks, testid: "nav-mijn-werk", module: "punten" },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, testid: "nav-kalender", module: "kalender" },
  { to: "/keuringen", label: "Keuringen", icon: ShieldCheck, testid: "nav-keuringen", module: "keuringen" },
  { to: "/locaties", label: "Locaties", icon: MapPin, testid: "nav-locaties" },
  { to: "/instellingen", label: "Instellingen", icon: Settings, testid: "nav-instellingen" },
];

const SidebarContent = ({ user, logout, canView, onNavigate, onPunt }) => (
  <div className="op-donker flex h-full flex-col bg-console text-white">
    <div className="flex items-center gap-2 border-b border-console-lijn px-4 py-4">
      {/* White tile, navy glyph. Never bg-primary: navy on navy is invisible. */}
      <div className="flex h-9 w-9 items-center justify-center rounded bg-white text-console">
        <Truck size={20} />
      </div>
      <div className="leading-tight">
        <div className="text-body font-semibold">TD-Ops</div>
        <div className="text-label text-mist">TW1 Group</div>
      </div>
    </div>

    <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
      {NAV_ITEMS.filter((item) => (!item.module || canView(item.module)) && (!item.roles || item.roles.includes(user?.role))).map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `relative flex items-center gap-3 rounded px-3 py-2 text-body transition-colors duration-150 ${
                isActive
                  ? "bg-console-actief font-semibold text-white before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:bg-borstel-licht before:content-['']"
                  : "text-white hover:bg-console-diep"
              }`
            }
          >
            <Icon size={20} />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
      {/* Quick capture is a nav item, never a floating button and never in the console.
          The dialog itself is mounted by AppShell, outside the nav Sheet. */}
      <PuntNavItem onOpen={onPunt} />
    </nav>

    <div className="border-t border-console-lijn p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-body font-medium" data-testid="sidebar-user-name">
            {user?.name}
          </div>
          <div className="truncate text-data text-mist">{user?.email}</div>
        </div>
        <RoleBadge role={user?.role} />
      </div>
      <Button
        data-testid="logout-button"
        variant="secondary"
        size="sm"
        className="w-full justify-center"
        onClick={logout}
      >
        <LogOut size={18} className="mr-2" /> Uitloggen
      </Button>
    </div>
  </div>
);

export const AppShell = () => {
  const { user, logout, canView, canWrite } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [puntOpen, setPuntOpen] = useState(false);
  const routeLoc = useLocation();
  const shell = useMemo(() => ({ openNav: () => setMobileOpen(true) }), []);

  // On a location page the punt is pre-filled with that location (read from the path).
  const match = routeLoc.pathname.match(/^\/locaties\/([^/]+)(?:\/|$)/);
  const presetLocationId = match && match[1] !== "nieuw" ? match[1] : null;

  // Opening quick capture from the phone nav closes the sheet first: a dialog
  // stacked inside an open nav sheet is its own trap.
  const openPunt = () => { setMobileOpen(false); setPuntOpen(true); };

  return (
    <ShellContext.Provider value={shell}>
      <BalkProvider>
        <div className="flex h-screen w-full overflow-hidden bg-background">
          <aside className="hidden w-64 shrink-0 border-r border-console-lijn lg:block">
            <SidebarContent user={user} logout={logout} canView={canView} onPunt={openPunt} />
          </aside>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent
              side="left"
              className="w-64 border-0 p-0 [&>button]:text-white"
              srTitle="Navigatie"
              srDescription="Kies een onderdeel van TD-Ops."
            >
              <SidebarContent
                user={user}
                logout={logout}
                canView={canView}
                onNavigate={() => setMobileOpen(false)}
                onPunt={openPunt}
              />
            </SheetContent>
          </Sheet>

          {/* Sibling of the Sheet, never inside it: Radix unmounts the sheet
              subtree on close and took the dialog with it. */}
          {canWrite() && (
            <PuntQuickDialog
              open={puntOpen}
              onOpenChange={setPuntOpen}
              presetLocationId={presetLocationId}
            />
          )}

          {/* No topbar. The console owns the top of every screen. */}
          <main className="min-w-0 flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </BalkProvider>
    </ShellContext.Provider>
  );
};
